"use client";

import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import Image from "next/image";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, BookOpen } from "lucide-react";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";

interface RelatedPostsProps {
  currentSlug: string;
  category: string;
  locationId?: string;
}

export function RelatedPosts({ currentSlug, category, locationId }: RelatedPostsProps) {
  const posts = useQuery(api.blog.getRelatedPosts, {
    currentSlug,
    category,
    locationId,
    limit: 3,
  });

  // Loading state
  if (posts === undefined) {
    return (
      <div className="mt-12 border-t border-[var(--roma-parchment-line)] pt-12">
        <div className="mb-6 flex items-center justify-between">
          <Skeleton className="h-8 w-48 bg-[var(--roma-linen)]" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden border-[var(--roma-parchment-line)] py-0">
              <Skeleton className="aspect-[16/9] w-full bg-[var(--roma-linen)]" />
              <CardContent className="p-4">
                <Skeleton className="mb-2 h-5 w-3/4 bg-[var(--roma-linen)]" />
                <Skeleton className="h-4 w-full bg-[var(--roma-linen)]" />
                <Skeleton className="mt-1 h-4 w-2/3 bg-[var(--roma-linen)]" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Empty state - no related posts found
  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="mt-12 border-t border-[var(--roma-parchment-line)] pt-12">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif text-2xl font-normal text-[var(--foreground)]">
          <BookOpen className="h-6 w-6 text-[var(--roma-terracotta)]" />
          Continue Reading
        </h3>
        <span className="text-sm text-[var(--foreground)]/58 md:hidden">
          Swipe →
        </span>
      </div>

      {/* Mobile: Horizontal scroll, Desktop: Grid */}
      <ScrollArea className="-mx-5 px-5 md:mx-0 md:px-0">
        <div className="flex w-max gap-4 pb-4 md:grid md:w-auto md:grid-cols-3 md:pb-0">
          {posts.map((post) => (
            <Link
              key={post._id}
              href={`/blog/${post.slug}`}
              className="min-w-[280px] md:min-w-0"
            >
              <Card className="group h-full overflow-hidden border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] py-0 shadow-none transition-all hover:border-[var(--roma-terracotta)]/40 hover:bg-white/55">
                {/* Thumbnail */}
                <div className="relative aspect-[16/9] overflow-hidden bg-[var(--roma-linen)]">
                  {post.coverImage ? (
                    <Image
                      src={optimizeCloudinaryUrl(post.coverImage, 400)}
                      alt={post.title}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      sizes="(max-width: 768px) 280px, 33vw"
                    />
                  ) : (
                    <div className="roma-paper-dark roma-grain flex h-full w-full items-center justify-center px-5 text-center">
                      <span className="font-serif text-xl italic text-[var(--roma-saffron)]/80">
                        Tuscan Cove Journal
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--roma-espresso)]/28 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

                  {/* Category badge overlay */}
                  <Badge
                    variant="secondary"
                    className="absolute top-3 left-3 border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]/86 text-xs text-[var(--roma-terracotta)] shadow-none backdrop-blur-sm"
                  >
                    {post.category}
                  </Badge>
                </div>

                {/* Content */}
                <CardContent className="p-4">
                  <h4 className="line-clamp-2 flex items-center gap-1.5 font-serif text-base font-normal text-[var(--foreground)] transition-colors group-hover:text-[var(--roma-terracotta)] md:text-lg">
                    {post.title}
                    <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                  </h4>
                  {post.excerpt && (
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[var(--foreground)]/62">
                      {post.excerpt}
                    </p>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="md:hidden" />
      </ScrollArea>
    </div>
  );
}
